import type { SxProps, Theme } from '@mui/material/styles';
import { alpha, styled } from '@mui/material/styles';
import { Box, Stack, Text } from 'components/ui';

const OTP_SETUP_QR_CODE_SIZE = 176;
const otpBodyLineHeight = 1.5;
const otpCompactUppercaseLineHeight = 1.15;
const otpTitleLineHeight = 1.2;

const RootPaper = styled(Box)({
  width: '100%',
  boxSizing: 'border-box',
  padding: 0,
});

const Title = styled(Text)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 32,
  fontWeight: 700,
  lineHeight: otpTitleLineHeight,
  margin: 0,
}));

const Subtitle = styled(Text)({
  color: 'rgba(0, 0, 0, 0.75)',
  fontSize: 16,
  lineHeight: otpBodyLineHeight,
  margin: 0,
});

const ActionButtons = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(2),
  justifyContent: 'flex-end',
  marginTop: theme.spacing(4),
  width: '100%',
}));

const otpActionButtonSx: SxProps<Theme> = {
  boxSizing: 'border-box',
  height: 40,
  minWidth: 112,
  padding: '12px 24px',
};

const SupportingMessage = styled(Text)(({ theme }) => ({
  color: theme.palette.grey[300],
  fontSize: 16,
  lineHeight: otpBodyLineHeight,
}));

const RememberDeviceLabel = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 16,
  lineHeight: otpBodyLineHeight,
}));

const OtpSetupContent = styled(Box)(({ theme }) => ({
  alignItems: 'stretch',
  columnGap: theme.spacing(3),
  display: 'flex',
  rowGap: theme.spacing(3),
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
}));

const OtpSetupQrCode = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  height: OTP_SETUP_QR_CODE_SIZE,
  justifyContent: 'center',
  minWidth: OTP_SETUP_QR_CODE_SIZE,
  width: OTP_SETUP_QR_CODE_SIZE,
  [theme.breakpoints.down('sm')]: {
    alignSelf: 'center',
  },
}));

const OtpSetupInstructions = styled('ol')({
  boxSizing: 'border-box',
  color: 'rgba(0, 0, 0, 0.75)',
  fontSize: 16,
  lineHeight: otpBodyLineHeight,
  margin: 0,
  paddingLeft: '24px',
  width: '100%',
});

const OtpSetupInstruction = styled('li')(({ theme }) => ({
  '& + &': {
    marginTop: theme.spacing(1),
  },
}));

const OtpSetupInstructionBody = styled(Box)(({ theme }) => ({
  alignItems: 'flex-start',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
}));

const OtpSetupInstructionText = styled('span')({
  color: 'rgba(0, 0, 0, 0.75)',
  display: 'inline',
  fontSize: 16,
  lineHeight: otpBodyLineHeight,
});

const OtpSetupKeyValue = styled('button')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  borderColor: theme.palette.primary.main,
  borderStyle: 'solid',
  borderWidth: 1,
  borderRadius: 4,
  color: theme.palette.primary.main,
  cursor: 'pointer',
  display: 'inline-flex',
  fontFamily: 'inherit',
  fontSize: 16,
  gap: theme.spacing(0.75),
  lineHeight: otpBodyLineHeight,
  margin: 0,
  padding: theme.spacing(0.25, 0.5),
  textAlign: 'left',
  transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
    duration: 280,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: 'fit-content',
  '&:focus-visible': {
    outline: `3px solid ${theme.palette.primary.light}`,
    outlineOffset: 2,
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
  '&:active': {
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
  '& .MuiSvgIcon-root': {
    color: 'currentColor',
    fontSize: 18,
  },
}));

const ShowHideOtpKeyButton = styled(Text)(({ theme }) => ({
  color: theme.palette.primary.main,
  cursor: 'pointer',
  borderRadius: 4,
  display: 'inline-flex',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: otpCompactUppercaseLineHeight,
  margin: 0,
  padding: theme.spacing(0.25, 0.5),
  textTransform: 'uppercase',
  transition: theme.transitions.create(['background-color', 'box-shadow', 'filter'], {
    duration: 220,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: 'fit-content',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    boxShadow: 'none',
    filter: 'none',
  },
  '&:active': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    boxShadow: 'none',
    filter: 'none',
  },
}));

export {
  RootPaper,
  OTP_SETUP_QR_CODE_SIZE,
  Title,
  Subtitle,
  ActionButtons,
  SupportingMessage,
  RememberDeviceLabel,
  OtpSetupContent,
  OtpSetupInstruction,
  OtpSetupInstructionBody,
  OtpSetupInstructions,
  OtpSetupInstructionText,
  OtpSetupKeyValue,
  OtpSetupQrCode,
  otpActionButtonSx,
  ShowHideOtpKeyButton,
};
